import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.js';
import { auth } from '../api/client.js';

export function TotpSetupPage() {
  const navigate = useNavigate();
  const { checkSession } = useAuth();
  const [step, setStep] = useState<'start' | 'verify'>('start');
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSetup = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await auth.totpSetup();
      setQrCode(result.qrCode);
      setSecret(result.secret);
      setStep('verify');
      console.log('[totp] setup initiated, QR code generated');
    } catch (err) {
      console.error('[totp] setup failed:', err);
      setError('Failed to generate 2FA setup. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (token.length !== 6) return;
    setLoading(true);
    setError('');
    try {
      await auth.totpVerify(token);
      console.log('[totp] 2FA enabled successfully');
      await checkSession();
      navigate('/', { replace: true });
    } catch (err) {
      console.error('[totp] verification failed:', err);
      setError('Invalid code. Please check your authenticator app and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card" style={{ maxWidth: 440 }}>
        <div className="brand" style={{ justifyContent: 'center', marginBottom: 24 }}>
          <div className="brand-mark" />
          <span className="brand-name">SubFi Pay</span>
        </div>

        <h1 className="login-title">Set up two-factor authentication</h1>
        <p className="login-subtitle">
          As a platform admin, you need to enable 2FA to secure your account.
        </p>

        {step === 'start' && (
          <button
            className="btn-sm btn-primary"
            style={{ width: '100%', padding: '12px', marginTop: 24, fontSize: 14 }}
            onClick={() => void handleSetup()}
            disabled={loading}
          >
            {loading ? 'Generating...' : 'Generate 2FA Code'}
          </button>
        )}

        {step === 'verify' && (
          <>
            <div className="totp-qr-wrap">
              {qrCode && <img src={qrCode} alt="TOTP QR Code" className="totp-qr" />}
            </div>
            <div className="totp-secret">
              <span className="totp-secret-label">Manual entry key:</span>
              <code className="totp-secret-code">{secret}</code>
            </div>
            <form onSubmit={(e) => void handleVerify(e)} className="totp-form">
              <label className="form-label">Enter the 6-digit code from your authenticator app</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={token}
                onChange={(e) => setToken(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                className="form-input totp-input"
                autoFocus
              />
              <button
                type="submit"
                className="btn-sm btn-primary"
                style={{ width: '100%', padding: '12px', fontSize: 14 }}
                disabled={loading || token.length !== 6}
              >
                {loading ? 'Verifying...' : 'Verify & Enable 2FA'}
              </button>
            </form>
          </>
        )}

        {error && <div className="form-error">{error}</div>}
      </div>
    </div>
  );
}
